"use client";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Spinner } from "@/components/ui/spinner";
import { REQUEST_PARAMS, UI } from "@/constants";
import { useSetQuery } from "@/hooks";
import { formatDateRange, getDefaultParams, getSeasonBounds, parseYmdLocal } from "@/lib";
import { addMonths, max, min } from "date-fns";
import { ChevronDownIcon } from "lucide-react";
import { useSearchParams } from "next/navigation";
import { memo, useEffect, useState } from "react";
import { DateRange } from "react-day-picker";
import { ActionsBlock } from ".";

const DEFAULT_PARAMS = getDefaultParams();

export const DatePicker = memo(() => {
  const searchParams = useSearchParams();
  const { setQuery, isPending } = useSetQuery();
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState<DateRange | undefined>(undefined);

  const dateFrom = searchParams.get(REQUEST_PARAMS.date_from) || DEFAULT_PARAMS.date_from;
  const dateTo = searchParams.get(REQUEST_PARAMS.date_to) || DEFAULT_PARAMS.date_to;

  useEffect(() => {
    const { seasonStart, seasonEnd } = getSeasonBounds(parseYmdLocal(dateFrom));

    const fromRaw = parseYmdLocal(dateFrom);
    const toRaw = parseYmdLocal(dateTo);

    const from = max([fromRaw, seasonStart]);
    const to = min([toRaw, seasonEnd]);

    setDate({ from, to });
  }, [dateFrom, dateTo]);

  const handleSubmit = () => {
    setQuery(formatDateRange(date!), { reset: true });
    setOpen(false);
  };

  const actions = [
    {
      key: UI.buttons.clear,
      Component: () => (
        <Button onClick={() => setDate(undefined)} size="sm" variant={"outline"}>
          {UI.buttons.clear}
        </Button>
      ),
    },
    {
      key: UI.buttons.select,
      Component: () => (
        <Button onClick={handleSubmit} size="sm" disabled={!date || !date.from || !date.to}>
          {UI.buttons.select}
        </Button>
      ),
    },
  ];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          disabled={isPending}
          className="w-34 flex justify-between"
        >
          {UI.labels.selectPeriod}
          {isPending ? <Spinner /> : <ChevronDownIcon />}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="end">
        <Calendar
          mode="range"
          numberOfMonths={2}
          defaultMonth={addMonths(date?.to ?? parseYmdLocal(DEFAULT_PARAMS.date_to), -1)}
          selected={date}
          onSelect={setDate}
          disabled={(d) => {
            const { seasonStart, seasonEnd } = getSeasonBounds(d);
            return d < seasonStart || d > seasonEnd;
          }}
        />
        <ActionsBlock actions={actions} className="p-4" />
      </PopoverContent>
    </Popover>
  );
});

DatePicker.displayName = "DatePicker";
